import { Component, computed, DestroyRef, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { displayName, optionalName } from '../../../../validators/name-rules';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import {
  arabicOnlyValidator,
  noLeadingTrailingSpacesValidator,
  noNumbersValidator,
  englishOnlyValidator,
} from '../cancellation-reason.validators';

import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { Loading } from '../../../../utils/loading/loading';
import { FilterInput } from '../../../../shared/ui/shared-filters/filter-input/filter-input';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { CancellationReasons } from '../../../../services/Cancellation Reasons/cancellation-reasons.service';
import { CancellationReasonError } from '../../../../pages/admin-dashboard/cancellation-reasons/cancellationReason.model';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { ScopeService } from '../../../../services/scope/scope-service';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { Error as AppError } from '../../../public-user-component/auth/shared/error/error';

@Component({
  selector: 'app-cancellation-reasons-edit-component',
  imports: [
    ConfirmationDialog,
    SuccessfulNotification,
    Loading,
    FilterInput,
    ReactiveFormsModule,
    SubmitButton,
    HasScopeDirective,
    AppError,
  ],
  templateUrl: './cancellation-reasons-edit-component.html',
  styleUrl: './cancellation-reasons-edit-component.scss',
})
export class CancellationReasonsEditComponent implements OnInit {
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private cancellationReasonsService = inject(CancellationReasons);
  private destroyRef = inject(DestroyRef);
  readonly SCOPES = SCOPES;
  isFormTouched = signal(false);
  submitAttempted = signal(false);
  private initialTitleAr = '';
  private initialTitleEn = '';

  reasonId = signal<number | null>(null);
  titleArValue = signal('');
  titleEnValue = signal('');
  isEditable = signal(false);

  isArabic = computed(() => this.locale.startsWith('ar'));
  isLoading = signal(false);
  Loading = $localize`:@@cancellationReasonLoading:Loading...`;

  titleArLabel = $localize`:@@cancellationReasonTitleArabic:Reason Title (Arabic)`;
  titleArPlaceholder = $localize`:@@cancellationReasonTitleArabicPlaceholder:Enter Arabic reason`;

  titleEnLabel = $localize`:@@cancellationReasonTitleEnglish:Reason Title (English)`;
  titleEnPlaceholder = $localize`:@@cancellationReasonTitleEnglishPlaceholder:Enter English reason (optional)`;

  cancelButton = $localize`:@@cancellationReasonCancel:Cancel`;
  submitButton = $localize`:@@editcancellation:Confirm`;

  showConfirmation = signal(false);

  confirmationTitle = $localize`:@@cancellationReasonEditConfirmationTitle:Confirm Update`;

  confirmationDescription = $localize`:@@cancellationReasonEditConfirmation:Are you sure you want to save these changes?`;

  // confirmButton = $localize`:@@cancellationReasonConfirm:Confirm`;
  confirmButton = signal($localize`:@@cancellationReasonConfirm:Confirm`);

  cancellationReasonFormedit = new FormGroup({
    titleAr: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(255),
      arabicOnlyValidator(),
      noLeadingTrailingSpacesValidator(),
      noNumbersValidator(),
    ]),

    titleEn: new FormControl('', [
      Validators.minLength(3),
      Validators.maxLength(255),
      englishOnlyValidator(),
      noLeadingTrailingSpacesValidator(),
      noNumbersValidator(),
    ]),
  });

  readonly titleArControl = computed(() => this.cancellationReasonFormedit.controls.titleAr);

  readonly titleEnControl = computed(() => this.cancellationReasonFormedit.controls.titleEn);

  titleAr = computed(() => this.titleArControl().value ?? '');
  titleEn = computed(() => this.titleEnControl().value ?? '');

  successMessage = signal<string | null>(null);

  ngOnInit() {
    const state = history.state;

    this.reasonId.set(state.id ?? null);
    this.titleArValue.set(state.titleAr ?? '');
    this.titleEnValue.set(state.titleEn ?? '');
    this.isEditable.set(state.isEditable ?? false);
    this.initialTitleAr = this.titleArValue();
    this.initialTitleEn = this.titleEnValue();

    this.cancellationReasonFormedit.patchValue({
      titleAr: this.titleArValue(),
      titleEn: this.titleEnValue(),
    });
  }

  onArabicChange(value: string | number | null) {
    const newValue = value?.toString() ?? '';

    this.titleArValue.set(newValue);
    this.submitAttempted.set(false);

    this.titleArControl().setValue(newValue);
    this.titleArControl().markAsTouched();
    this.titleArControl().updateValueAndValidity();
  }

  onEnglishChange(value: string | number | null) {
    const newValue = value?.toString() ?? '';

    this.titleEnValue.set(newValue);
    this.submitAttempted.set(false);

    this.titleEnControl().setValue(newValue);
    this.titleEnControl().markAsTouched();
    this.titleEnControl().updateValueAndValidity();
  }

  submitForm() {
    this.cancellationReasonFormedit.markAllAsTouched();
    this.submitAttempted.set(true);

    const { titleAr, titleEn } = this.cancellationReasonFormedit.getRawValue();
    const hasChanges = (titleAr ?? '') !== this.initialTitleAr || (titleEn ?? '') !== this.initialTitleEn;
    this.isFormTouched.set(hasChanges);

    if (!hasChanges || this.cancellationReasonFormedit.invalid) {
      return;
    }
    this.errorMessage.set('');
    this.showConfirmation.set(true);
  }

  cancel() {
    this.router.navigate(['/admin/cancellation']);
  }

  errorMessage = signal('');
  confirmCreate() {
    this.isLoading.set(true);
    this.confirmButton.set(this.Loading);
    const reasonId = this.reasonId();
    const data = this.cancellationReasonFormedit.getRawValue();

    const dto = {
      titleAr: data.titleAr ?? '',
      titleEn: optionalName(data.titleEn),
    };

    const subscription = this.cancellationReasonsService.updateCancellationReason(reasonId!, dto).subscribe({
      next: () => {
        const reasonName = displayName(this.isArabic(), dto.titleAr, dto.titleEn);

        this.successMessage.set(
          `${$localize`:@@cancellationReasonUpdatedSuccessfully:Cancellation reason`} "${reasonName}" ${$localize`:@@cancellationReasonUpdatedSuccessfully2:updated successfully`}`,
        );

        this.showConfirmation.set(false);
        this.isLoading.set(false);
        this.confirmButton.set($localize`:@@cancellationReasonConfirm:Confirm`);

        setTimeout(() => {
          this.router.navigate(['/admin/cancellation']);
        }, 3000);
      },

      error: (error: CancellationReasonError) => {
        this.isLoading.set(false);
        this.confirmButton.set($localize`:@@cancellationReasonConfirm:Confirm`);
        if (error.code === 'REST-0004' && error.reqBodyErrors) {
          const hasArabicError = error.reqBodyErrors.some((error) => error.includes('Field: titleAr'));

          const hasEnglishError = error.reqBodyErrors.some((error) => error.includes('Field: titleEn'));

          if (hasArabicError && hasEnglishError) {
            this.errorMessage.set(
              this.isArabic()
                ? 'اسم السبب باللغة العربية والإنجليزية غير صالح.'
                : 'The Arabic and English reason names are invalid.',
            );

            return;
          }

          if (hasArabicError) {
            this.errorMessage.set(
              this.isArabic() ? 'اسم السبب باللغة العربية غير صالح.' : 'The Arabic reason name is invalid.',
            );

            return;
          }

          if (hasEnglishError) {
            this.errorMessage.set(
              this.isArabic() ? 'اسم السبب باللغة الإنجليزية غير صالح.' : 'The English reason name is invalid.',
            );

            return;
          }
        }
        if (error.code === 'REST-0001') {
          this.errorMessage.set(this.isArabic() ? 'حدث خطأ ما.' : 'Something went wrong.');

          return;
        }
        this.errorMessage.set(
          this.isArabic() ? error.messageAr || error.messageEn : error.messageEn || error.messageAr,
        );
      },
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  closeConfirmation() {
    this.showConfirmation.set(false);
  }
}
